import { useTenant } from "@/lib/tenant-context.js";
import { useAsync } from "@/hooks/use-async.js";
import { Skeleton } from "@/components/ui/skeleton.js";
import { Badge } from "@/components/ui/badge.js";
import { DataTable } from "@/views/data-table.js";
import type { AuditRecord, OperatorAuditRecord } from "@/lib/types.js";

interface UnifiedAuditRow {
  id: string;
  source: "tenant" | "operator";
  created_at: string;
  actor_type: string;
  actor_id: string;
  action: string;
  entity_summary: string;
  channel?: string;
}

/**
 * Unified audit-log view. The backend returns two arrays — `data` for the
 * per-tenant `audit_log` (per-PRD shape) and `operator` for the cross-tenant
 * `public.operator_audit_log` (different columns). Both are merged into one
 * timeline with a `source` discriminator so the operator events stay visible
 * to tenant staff per PRD §"Tenant staff can view operator events that
 * affected their tenant".
 */
export function AuditLogPage(): React.JSX.Element {
  const { api } = useTenant();
  const audit = useAsync(() => api.auditLog(), [api]);
  if (audit.loading) return <Skeleton className="h-72 w-full" />;

  const tenantRows: UnifiedAuditRow[] = (audit.data?.data ?? []).map(
    (r: AuditRecord): UnifiedAuditRow => ({
      id: r.id,
      source: "tenant",
      created_at: r.created_at,
      actor_type: r.actor_type,
      actor_id: r.actor_id,
      action: r.action,
      entity_summary: `${r.entity}${r.entity_id ? ` (${r.entity_id.slice(0, 8)})` : ""}`,
      channel: r.channel
    })
  );
  const operatorRows: UnifiedAuditRow[] = (audit.data?.operator ?? []).map(
    (r: OperatorAuditRecord): UnifiedAuditRow => ({
      id: r.id,
      source: "operator",
      created_at: r.created_at,
      actor_type: "operator",
      actor_id: r.operator_actor_id,
      action: r.action,
      entity_summary: r.tenant_schema ? `tenant ${r.tenant_schema}` : "platform",
      channel: "admin"
    })
  );
  const rows = [...tenantRows, ...operatorRows].sort((a, b) =>
    b.created_at.localeCompare(a.created_at)
  );

  return (
    <div className="flex flex-col gap-3">
      <h1 className="text-lg font-semibold">Audit log</h1>
      <DataTable<UnifiedAuditRow>
        rows={rows}
        getRowKey={(r) => `${r.source}:${r.id}`}
        columns={[
          { header: "When", cell: (r) => new Date(r.created_at).toLocaleString() },
          {
            header: "Source",
            cell: (r) => (
              <Badge variant={r.source === "operator" ? "warning" : "outline"}>
                {r.source}
              </Badge>
            )
          },
          {
            header: "Actor",
            cell: (r) => (
              <span className="font-mono text-xs">
                <Badge variant="outline" className="mr-1">
                  {r.actor_type}
                </Badge>
                {r.actor_id}
              </span>
            )
          },
          { header: "Action", cell: (r) => <code className="text-xs">{r.action}</code> },
          { header: "Entity", cell: (r) => r.entity_summary }
        ]}
      />
    </div>
  );
}
